import { toSearch } from "utils/queryParams";

/**
 * What one Ask turn is allowed to draw on.
 *
 * One vocabulary, where there used to be two. A **corpus** was a shelf of indexed documents and
 * rode as `resource_apps`; a **tool** was a live lookup and rode as `index_slug` or `tools`. A
 * reader ticking boxes had to know which of the two they were looking at, and the rail had to
 * keep them apart because the wire could only carry one at a time.
 *
 * A **scope** is both. `GET /api/agent/` reports the ones a reader may ask under, each already
 * carrying its label, its one-line hint, the tools it attaches and the shelves it reads. The
 * server decides what a scope contains and who may have it; this module only decides what the
 * URL says and what the picker renders.
 */

/**
 * The Ask URL contract, shared by the page and the rail beside it.
 *
 * They are siblings with no state between them, and deliberately so: the URL is what they
 * share, which is what makes a thread a link. It carries the session, the citation it was
 * opened from, and the scopes it was asked under.
 */
export const ASK_URL_SCHEMA = {
    session_id: "string",
    resource: "string",
    /**
     * A question handed in from another surface, so the reader does not have to restate it
     * here. Asked once on arrival and then stripped, because a question left in the address bar
     * would be re-asked on every refresh.
     */
    query: "string",
    scopes: "array",
    /**
     * The Published filter, as dates (YYYY-MM-DD). Either end may be open; neither means any
     * time. Dates rather than a preset name, so "past 30 days" in a shared link means the same
     * thirty days to whoever opens it.
     */
    published_from: "string",
    published_to: "string",
};

/**
 * The scopes this reader may ask under, as the server reported them.
 *
 * No fallback list and no local label table. There used to be both, and they were how a label
 * for a tool that did not exist got written down: the client cannot know what the catalog holds,
 * so a scope with no label renders as its own code -- ugly and honest.
 */
export const availableScopes = (scopes = []) =>
    (scopes || [])
        .filter((scope) => scope && scope.code)
        .map((scope) => ({
            code: String(scope.code),
            label: String(scope.label || scope.code),
            hint: String(scope.hint || ""),
            /* Which shelf of the picker it sits on. Blank on an arbo-api that does not group them. */
            group: String(scope.group || ""),
            /* The newest document the source holds, for the Beta notice. Null where unknown. */
            currentThrough: scope.current_through || null,
        }));

/**
 * The picker's shelves, in the order the server listed their first member.
 *
 * A catalog with no groups at all comes back as one unnamed shelf, which the rail renders as the
 * flat list it used to be rather than under a heading it would have to invent.
 */
export const groupScopes = (scopes = []) => {
    const groups = [];
    const byName = new Map();
    scopes.forEach((scope) => {
        const name = scope.group || "";
        if (!byName.has(name)) {
            const group = { name, scopes: [] };
            byName.set(name, group);
            groups.push(group);
        }
        byName.get(name).scopes.push(scope);
    });
    return groups;
};

/** Labels for scope codes, in the order given; a code with no label is shown as itself. */
export const scopeLabels = (codes = [], scopes = []) =>
    (codes || []).map((code) => scopes.find((scope) => scope.code === code)?.label || code);

/**
 * Distinct, order-preserving, and only scopes the reader actually has.
 *
 * A selection that outlived what made it valid -- a lapsed entitlement, a shared link, a scope
 * withdrawn on the server -- is narrowed to what the reader has rather than shown as ticked.
 * arbo-api drops it from the turn anyway; dropping it here means the boxes say what the next
 * turn will really do.
 */
export const sanitizeScopeSelection = (selected = [], available = []) => {
    const offered = new Set((available || []).map((scope) => scope.code));
    const seen = new Set();
    return (selected || [])
        .map((code) => String(code || "").trim())
        .filter((code) => code && offered.has(code) && !seen.has(code) && seen.add(code));
};

/**
 * A link into Ask, scoped to the surface it is leaving.
 *
 * `extra` carries what the reader was looking at -- `resource` for a cited page, `query` for a
 * question already typed, `session_id` for a thread being resumed.
 *
 * The surface names its scope directly now. There used to be a table mapping one to the other,
 * because a tool surface and a corpus surface wrote different URL params; with one vocabulary
 * the mapping is the identity, and a table that says `x -> x` is a place for them to drift.
 */
export const askHref = (scope, extra = {}) => `/ask${toSearch({ ...(scope ? { scopes: [scope] } : {}), ...extra })}`;
